import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getConfig } from '../api/config';
import { exchangeCode } from '../auth/oidc';
import { useAuth } from '../auth/useAuth';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const exchanged = useRef(false);

  useEffect(() => {
    if (exchanged.current) {
      return;
    }
    exchanged.current = true;

    const params = new URLSearchParams(window.location.search);

    // Standard OIDC error redirect (RFC 6749 §4.1.2.1) — e.g. Zitadel refusing
    // an account without a project grant. Surface it instead of a dead end.
    const oidcError = params.get('error');
    if (oidcError) {
      const description = params.get('error_description');
      setError(
        oidcError === 'access_denied' || /grant|not.?authorized/i.test(description ?? '')
          ? 'This account is not authorized for SkyMap Docs. Choose a different account, or ask an admin to grant it access.'
          : `Sign-in failed: ${description ?? oidcError}`,
      );
      return;
    }

    const code = params.get('code');
    const state = params.get('state');
    if (!code) {
      setError('No authorisation code received.');
      return;
    }

    void (async () => {
      try {
        await exchangeCode(await getConfig(), code, state);
        refreshAuth();
        navigate('/admin', { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
      }
    })();
  }, [navigate, refreshAuth]);

  if (error) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center' }}>
        <p style={{ color: 'var(--danger-text)' }}>{error}</p>
        <a href="/login">Sign in with a different account</a>
      </div>
    );
  }

  return (
    <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text-muted)' }}>
      <p>Signing in…</p>
    </div>
  );
}
